import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { ShieldCheck, AlertTriangle, RefreshCw, Check, X } from "lucide-react";

const SC_TINT = {
  "SMPL":    "#F59E0B", "EVFM":   "#10B981", "MEDL":    "#DC2626",
  "AIOF":    "#9333EA", "SMIA":   "#0EA5E9", "CIDSA":   "#16A34A",
  "ESDM":    "#F97316", "3CI":    "#EF4444", "READ":    "#0891B2",
  "IoT-CPS": "#7C3AED",
};

export default function AdminGovernance() {
  const [data, setData] = useState(null);
  const [rejected, setRejected] = useState([]);
  const [domains, setDomains] = useState([]);
  const [msg, setMsg] = useState("");
  const [overrideRow, setOverrideRow] = useState(null);

  const load = async () => {
    try {
      const [d, r, gd] = await Promise.all([
        api.get("/admin/governance/dashboard"),
        api.get("/admin/jobs/rejected", { params: { limit: 50 } }),
        api.get("/governance/domains"),
      ]);
      setData(d.data); setRejected(r.data); setDomains(gd.data.domains || []);
    } catch (e) { setMsg(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setMsg("Syncing...");
    try {
      const r = await api.post("/admin/jobs/sync");
      setMsg(`Sync done — kept ${r.data.kept}, rejected ${r.data.rejected_restricted + r.data.rejected_no_domain} of ${r.data.total_pulled}`);
      load();
    } catch (e) { setMsg(formatError(e)); }
  };

  const submitOverride = async (row, sc_domain, sc_role, note) => {
    try {
      await api.post("/admin/governance/override", {
        source: row.source, source_id: row.source_id,
        sc_domain, sc_role, note,
      });
      setMsg(`Promoted ${row.title || row.source_id} → ${sc_domain}`);
      setOverrideRow(null);
      load();
    } catch (e) { setMsg(formatError(e)); }
  };

  if (!data) return <div className="card card-pad">Loading governance metrics…</div>;
  const totals = data.totals || {};
  const maxDom = Math.max(1, ...(data.by_domain || []).map(d => d.count));

  return (
    <div className="space-y-6" data-testid="admin-governance">
      <div className="card card-pad flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="eyebrow">SkillConnect Governance</span>
          <h2 className="font-display font-black text-3xl mt-1">Domain compliance</h2>
          <p className="text-muted mt-1 text-sm">Live snapshot of the 10 approved SC domain filter — kept / rejected / overridden across all ingestion paths.</p>
        </div>
        <button onClick={syncNow} className="btn-primary" data-testid="gov-sync"><RefreshCw size={14}/> Re-sync crawler</button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <KPI label="Approval rate" value={`${totals.approval_rate ?? 0}%`} accent="#10B981" testId="gov-approval-rate"/>
        <KPI label="Active jobs" value={totals.kept ?? 0} accent="#006CFF" testId="gov-kept"/>
        <KPI label="Total rejected" value={totals.rejected ?? 0} accent="#DC2626" testId="gov-rejected"/>
        <KPI label="Active postings" value={(data.posting_compliance || {}).active ?? 0} accent="#7C3AED" testId="gov-active-postings"/>
      </div>

      {/* Per-domain heat-map */}
      <div className="card card-pad" data-testid="gov-by-domain">
        <span className="eyebrow">Active jobs per domain</span>
        <h3 className="font-display text-xl font-bold mt-1 mb-4">Heat-map</h3>
        <div className="space-y-2">
          {(data.by_domain || []).map(d => (
            <div key={d.sc_domain} className="flex items-center gap-3 text-sm">
              <span className="w-16 font-bold text-xs" style={{ color: SC_TINT[d.sc_domain] }}>{d.sc_domain}</span>
              <span className="w-52 text-muted text-xs">{d.name}</span>
              <div className="flex-1 h-3 rounded-full bg-paper2 overflow-hidden">
                <div className="h-full rounded-full" style={{
                  width: `${(d.count / maxDom) * 100}%`,
                  background: SC_TINT[d.sc_domain] || "#0040A8",
                }} />
              </div>
              <span className="w-10 text-right text-xs font-bold tabular-nums">{d.count}</span>
            </div>
          ))}
          {!(data.by_domain || []).length && <p className="text-muted text-sm">No allowed jobs yet — run a sync.</p>}
        </div>
      </div>

      {/* Rejection reasons */}
      <div className="card card-pad">
        <span className="eyebrow">Top rejection reasons (last 30d)</span>
        <h3 className="font-display text-xl font-bold mt-1 mb-4">Why jobs are being filtered out</h3>
        <table className="dense">
          <thead><tr><th>Reason</th><th className="text-right">Count</th></tr></thead>
          <tbody>
            {(data.rejection_reasons || []).map((r) => (
              <tr key={r.reason}>
                <td className="text-sm">
                  {r.reason?.includes("Restricted") ? (
                    <span className="inline-flex items-center gap-1.5 text-red-700"><X size={13}/> {r.reason}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-yellow-700"><AlertTriangle size={13}/> {r.reason}</span>
                  )}
                </td>
                <td className="text-right tabular-nums">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recently rejected (overridable) */}
      <div className="card card-pad" data-testid="gov-rejected-list">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="eyebrow">Last 50 rejected jobs</span>
            <h3 className="font-display text-xl font-bold mt-1">Manual override</h3>
          </div>
          <span className="text-xs text-muted">{rejected.length} rows</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dense">
            <thead><tr><th>Title</th><th>Company</th><th>Source</th><th>Reason</th><th className="text-right">Override</th></tr></thead>
            <tbody>
              {rejected.map((r, i) => (
                <tr key={`${r.source}-${r.source_id}-${i}`} data-testid={`rej-row-${i}`}>
                  <td className="text-sm font-semibold">{r.title || "—"}</td>
                  <td className="text-sm text-muted">{r.company || "—"}</td>
                  <td><span className="badge">{r.source}</span></td>
                  <td className="text-xs text-muted">{(r.reason || "").slice(0, 60)}</td>
                  <td className="text-right">
                    <button className="btn-ghost text-xs" onClick={() => setOverrideRow(r)} data-testid={`override-${i}`}>
                      Promote
                    </button>
                  </td>
                </tr>
              ))}
              {!rejected.length && <tr><td colSpan={5} className="text-muted text-sm">No rejected jobs in the buffer.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted" data-testid="gov-msg">{msg}</p>

      {overrideRow && (
        <OverrideModal row={overrideRow} domains={domains} onClose={() => setOverrideRow(null)} onSubmit={submitOverride} />
      )}
    </div>
  );
}

function KPI({ label, value, accent, testId }) {
  return (
    <div className="card card-pad" data-testid={testId}>
      <span className="eyebrow">{label}</span>
      <div className="font-display font-black text-4xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function OverrideModal({ row, domains, onClose, onSubmit }) {
  const [sc, setSc] = useState("");
  const [role, setRole] = useState("");
  const [note, setNote] = useState("");
  const picked = domains.find(d => d.id === sc);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-paper rounded-xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-display font-bold text-xl mb-3">Promote rejected job</h3>
        <p className="text-sm text-muted mb-4">
          <span className="font-semibold text-default">{row.title}</span>
          <br/>Was rejected for: <span className="text-red-700">{row.reason}</span>
        </p>
        <label className="eyebrow">Assign SC domain</label>
        <select className="input mt-1" value={sc} onChange={e => { setSc(e.target.value); setRole(""); }} data-testid="override-sc-domain">
          <option value="">Select a domain…</option>
          {domains.map(d => <option key={d.id} value={d.id}>{d.id} — {d.name}</option>)}
        </select>
        {picked && (
          <>
            <label className="eyebrow mt-3 block">Map to role</label>
            <select className="input mt-1" value={role} onChange={e => setRole(e.target.value)} data-testid="override-sc-role">
              <option value="">(auto)</option>
              {picked.allowed_roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </>
        )}
        <label className="eyebrow mt-3 block">Note (optional)</label>
        <textarea className="input mt-1 min-h-[60px]" value={note} onChange={e => setNote(e.target.value)} data-testid="override-note"/>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose} data-testid="override-cancel">Cancel</button>
          <button className="btn-primary" disabled={!sc} onClick={() => onSubmit(row, sc, role, note)} data-testid="override-submit">
            <Check size={14}/> Promote to {sc || "…"}
          </button>
        </div>
      </div>
    </div>
  );
}
